/**
 * Вкладка «Запись» карточки сделки: когда и кто записал клиента, на какой час,
 * какой была загрузка этого часа в салоне ДО записи и нарушен ли регламент
 * (вопросы 578 и 592 учебного центра: выходные 12–16 и «рыжий/красный» час).
 * Спека `docs/specs/2026-09-25-zapis-slots-design.md`; те же данные —
 * `prokat-runner deal-zapis ID`.
 */
import { useQuery } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";

import { type DealZapis, type ZapisEvent, type ZapisLevel, type ZapisSlot, loadDealZapis } from "@/lib/dealZapis";

const dt = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${iso.slice(11, 16)}`;
const when = (s: string | null) => (s ? `${s.slice(8, 10)}.${s.slice(5, 7)} ${s.slice(11, 16)}` : "—");

const LEVEL: Record<ZapisLevel, { label: string; tone: string }> = {
  red: { label: "красный", tone: "bg-err-bg text-err" },
  orange: { label: "рыжий", tone: "bg-warn-bg text-warn" },
  ok: { label: "норма", tone: "bg-ok-bg text-ok" },
  none: { label: "нет расчёта", tone: "bg-bg-subtle text-fg-muted" },
};

function Pill({ tone, children }: { tone: string; children: ReactNode }) {
  return <span className={`rounded px-1.5 py-px text-[11.5px] ${tone}`}>{children}</span>;
}

function Stat({ label, value, tone = "" }: { label: string; value: ReactNode; tone?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3" data-annot={`запись «${label}»`}>
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className={`text-[20px] font-semibold leading-none tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}

/** Час салона одной строкой: загрузка, стилисты, записи, свободные места. */
function Slot({ s, label }: { s: ZapisSlot | null; label: string }) {
  if (!s) return <div className="text-fg-faint">{label}: нет расчёта — час вне работы салона или дальше 14 дней вперёд</div>;
  return (
    <div className="tabular-nums">
      <span className="text-fg-muted">{label}:</span> <b>{s.load}%</b> · стилистов {s.stylists}
      {s.avg && <span className="text-fg-faint" title="график не заполнен — CRM берёт среднее по дню недели"> (по среднему)</span>}, запас {s.zapas} · записей{" "}
      {s.cur}
      {s.prev ? ` (+${s.prev} с прошлого часа)` : ""} · мест с запасом {s.full_optimal}, всего {s.full_max}
    </div>
  );
}

function Event({ e }: { e: ZapisEvent }) {
  const head = (
    <span className="whitespace-nowrap pt-px text-[12px] tabular-nums text-fg-faint">
      {dt(e.at)} · {e.user ?? "—"}
    </span>
  );
  if (e.kind === "stage") {
    return (
      <li className="flex flex-wrap gap-x-3 text-[12.5px]" data-annot="запись: стадия">
        {head}
        <span>
          стадия {e.old || "—"} → <b className={e.to_wait ? "" : "font-normal"}>{e.new || "—"}</b>
        </span>
      </li>
    );
  }
  if (e.kind === "clear") {
    return (
      <li className="flex flex-wrap gap-x-3 text-[12.5px] text-fg-muted" data-annot="запись: снята">
        {head}
        <span>время записи снято (было {when(e.old)})</span>
      </li>
    );
  }
  const local = e.new_local ? <span className="text-fg-faint"> ({e.new_local.slice(11, 16)} местного)</span> : null;
  const source = e.source === "whatsapp" && <Pill tone="bg-bg-subtle text-fg-muted">окно WhatsApp «след. обработка»</Pill>;
  if (e.kind === "followup") {
    return (
      <li className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-fg-muted" data-annot="запись: дата обработки">
        {head}
        <span>
          дата обработки <span className="tabular-nums">{when(e.new)}</span>
          {local}
          {e.old && <> (было {when(e.old)})</>}
        </span>
        {source}
        <span className="text-fg-faint">{e.reason}</span>
      </li>
    );
  }
  const v = e.verdict;
  return (
    <li
      className={`flex flex-col gap-1 rounded-[var(--radius-card)] border p-3 text-[12.5px] ${v.violation ? "border-err/40" : "border-border"}`}
      data-annot="запись: на время"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {head}
        <span>
          запись на <b className="tabular-nums">{when(e.new)}</b>
          {local}
          {e.old && <span className="text-fg-muted"> (было {when(e.old)})</span>}
        </span>
        {source}
        <span className="text-fg-muted">{e.location ?? "салон ?"}</span>
        {e.category && <Pill tone="bg-bg-subtle text-fg-muted">{e.category}</Pill>}
        {e.hour && <Pill tone={LEVEL[v.level].tone}>{LEVEL[v.level].label}</Pill>}
        {v.top && <Pill tone="bg-warn-bg text-warn">пик выходных</Pill>}
        {v.violation && <Pill tone="bg-err-bg text-err">нарушение</Pill>}
      </div>
      {e.hour ? (
        <>
          <Slot s={e.before} label={`час ${e.hour.slice(11, 16)} до записи`} />
          {e.after && <Slot s={e.after} label="после" />}
          {v.status_name && <div className="text-fg-faint">статус слота CRM: {v.status_name}</div>}
          {v.reasons.map((r) => (
            <div key={r} className="flex items-center gap-1 text-err">
              <TriangleAlert size={13} aria-hidden /> {r}
            </div>
          ))}
        </>
      ) : (
        <div className="text-fg-faint">дата без времени — в слоты не входит</div>
      )}
    </li>
  );
}

function Body({ z }: { z: DealZapis }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-annot="запись: итог">
        <Stat label="Записей на время" value={z.total.bookings} />
        <Stat label="Нарушений" value={z.total.violations} tone={z.total.violations ? "text-err" : "text-ok"} />
        <Stat label="Без расчёта слота" value={z.total.no_data} />
      </div>
      <div className="text-[12px] text-fg-muted" data-annot="запись: регламент">
        Регламент учебного центра:{" "}
        {z.rules.map((r, i) => (
          <span key={r.id}>
            {i > 0 && " · "}
            <a className="underline" href={r.url} target="_blank" rel="noreferrer">
              вопрос {r.id}
            </a>
          </span>
        ))}{" "}
        — выходные 12–16 и «рыжий/красный» час: не отказываем, но сначала предлагаем спокойное время.
      </div>
      {z.events.length ? (
        <ol className="flex flex-col gap-2" data-annot="запись: лента">
          {z.events.map((e, i) => (
            <Event key={i} e={e} />
          ))}
        </ol>
      ) : (
        <div className="text-[12.5px] text-fg-muted">В журнале сделки нет ни записей, ни «ждем клиента».</div>
      )}
    </>
  );
}

export function DealZapisTab({ dealId }: { dealId: number }) {
  const q = useQuery({ queryKey: ["deal-zapis", dealId], queryFn: () => loadDealZapis(dealId) });
  if (q.isPending) return <div className="text-[12.5px] text-fg-muted">Восстанавливаю загрузку салона на момент записи…</div>;
  if (q.isError) return <div className="text-[12.5px] text-err">Не удалось получить записи: {String(q.error.message)}</div>;
  return <Body z={q.data} />;
}
